import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  LinearProgress,
  Avatar,
  Stack,
  Divider
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  AutoAwesome as AutoAwesomeIcon,
  FamilyRestroom as FamilyIcon,
  Psychology as AiIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import api from '../services/api';
import { getFamily, attributeFamilyTransaction } from '../services/api';
import { getBudgets } from '../services/budgetService';
import { useError } from '../contexts/ErrorContext';

/** Session length for the open-ended "Teach Buddy" queue — matches mobile QUEUE_SIZE. */
const REVIEW_QUEUE_LIMIT = 20;

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '20px',
    width: '100%',
    maxWidth: 600,
    maxHeight: '80vh'
  }
}));

const TransactionRow = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 'none'
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04)
  },
  flexDirection: 'column',
  alignItems: 'stretch'
}));

const ConfidenceBadge = styled(Chip)(({ theme, confidence }) => {
  const getColor = () => {
    if (confidence >= 80) return theme.palette.success.main;
    if (confidence >= 50) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return {
    backgroundColor: alpha(getColor(), 0.1),
    color: getColor(),
    fontWeight: 600,
    fontSize: '0.75rem'
  };
});

const MerchantLogo = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
  marginRight: theme.spacing(2),
  backgroundColor: alpha(theme.palette.primary.main, 0.1)
}));

const MemberButton = styled(Button)(({ theme }) => ({
  borderRadius: '50px',
  textTransform: 'none',
  minWidth: 'auto',
  padding: theme.spacing(0.5, 2)
}));

const ReviewCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2)
}));

const AI_ASSIGNERS = new Set(['ai', 'ai_service']);

/**
 * Confidence for a queue row: AI-assigned category first, then bare suggestion.
 * Legacy rows stored 0–100; modern rows are 0–1.
 */
const resolveConfidencePct = (transaction) => {
  const assigned = transaction?.budgetCategory;
  const hasAiCategory = Boolean(
    assigned?.name && assigned?.assignedBy && AI_ASSIGNERS.has(String(assigned.assignedBy))
  );
  const raw = hasAiCategory
    ? (assigned?.aiConfidence ?? assigned?.confidence)
    : transaction?.aiSuggestion?.confidence;
  if (typeof raw !== 'number' || Number.isNaN(raw)) return null;
  const normalized = raw > 1 ? raw : raw * 100;
  return Math.round(normalized);
};

const resolveSuggestedCategoryName = (transaction) => {
  const assigned = transaction?.budgetCategory;
  if (assigned?.name && assigned?.assignedBy && AI_ASSIGNERS.has(String(assigned.assignedBy))) {
    return assigned.name;
  }
  return transaction?.aiSuggestion?.categoryName || null;
};

/**
 * TransactionReviewModal - Modal for reviewing AI categorizations or family attribution.
 *
 * Low-confidence / Teach Buddy mode (#1343 / #957 slice 2) pulls the confidence-ranked
 * review queue (`GET /transactions/review-queue`) — no threshold — so wrong-but-confident
 * predictions still surface after personal-model training. Keep confirms the AI pick;
 * Fix opens a category picker. Both write `POST /ai/feedback/:id` so the item leaves the
 * shared queue on web and mobile. ArrowRight = keep, ArrowLeft = fix (button parity too).
 * Completing a sitting does not resolve the Activity alert or claim "all reviewed" while
 * `queueTotal > initialCount` (#1473); remaining-count copy matches mobile.
 *
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {string[]} transactionIds - Used for attribution mode only (low-confidence ignores)
 * @param {string} alertId - Optional alert ID to dismiss when review is complete
 * @param {function} onAllReviewed - Callback when all transactions are reviewed
 * @param {function} onTransactionReviewed - Callback when a single transaction is reviewed
 * @param {'low-confidence' | 'attribution'} reviewType - Type of review to perform
 */
const TransactionReviewModal = ({
  open,
  onClose,
  transactionIds = [],
  alertId,
  onAllReviewed,
  onTransactionReviewed,
  reviewType = 'low-confidence'
}) => {
  const { showError, showSuccess } = useError();
  const [transactions, setTransactions] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState({});
  const [initialCount, setInitialCount] = useState(0);
  const [queueTotal, setQueueTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [activeTransactionId, setActiveTransactionId] = useState(null);

  // Get active family members
  const activeMembers = useMemo(
    () => (familyMembers || []).filter((member) => member.status === 'active'),
    [familyMembers]
  );

  const currentTransaction = transactions[0] || null;
  const isQueueMode = reviewType !== 'attribution';
  const isProcessingCurrent = Boolean(
    currentTransaction && processing[currentTransaction._id]
  );

  // Stable key so a default `transactionIds=[]` (new array each render) does not re-fetch.
  const transactionIdsKey = useMemo(
    () => (Array.isArray(transactionIds) ? transactionIds.join(',') : ''),
    [transactionIds]
  );

  // Fetch transactions (and supporting data) when modal opens
  useEffect(() => {
    if (!open) {
      setTransactions([]);
      setFamilyMembers([]);
      setInitialCount(0);
      setQueueTotal(0);
      setCategories([]);
      setCategoryPickerOpen(false);
      setActiveTransactionId(null);
      return undefined;
    }

    let cancelled = false;
    const ids = transactionIdsKey ? transactionIdsKey.split(',') : [];

    const loadData = async () => {
      setLoading(true);
      try {
        if (reviewType === 'attribution') {
          const [txnResponse, familyResponse] = await Promise.all([
            ids.length > 0
              ? api.get(`/transactions?ids=${ids.join(',')}&showLowConfidence=true`)
              : api.get('/transactions/family/unattributed'),
            getFamily()
          ]);

          if (cancelled) return;

          const txns = ids.length > 0
            ? (txnResponse?.data?.transactions || [])
            : (txnResponse?.data?.data || txnResponse?.data || []);
          setTransactions(txns);
          setInitialCount(txns.length);
          setQueueTotal(txns.length);
          setFamilyMembers(familyResponse?.data?.family?.familyMembers || familyResponse?.family?.familyMembers || []);
        } else {
          // Confidence-ranked queue (#957 / #1343) — no threshold, limit matches mobile.
          const [queueResponse, budgets] = await Promise.all([
            api.get('/transactions/review-queue', {
              params: { limit: REVIEW_QUEUE_LIMIT }
            }),
            getBudgets().catch(() => [])
          ]);

          if (cancelled) return;

          const payload = queueResponse?.data?.data || {};
          const txns = Array.isArray(payload.transactions) ? payload.transactions : [];
          const total = typeof payload.total === 'number' ? payload.total : txns.length;
          setTransactions(txns);
          setInitialCount(txns.length);
          setQueueTotal(total);

          const budgetList = Array.isArray(budgets) ? budgets : [];
          const active = budgetList.find((b) => b.isActive) || budgetList[0];
          const cats = (active?.categories || [])
            .map((c) => ({
              id: c._id || c.id,
              name: c.name,
              icon: c.icon,
              color: c.color
            }))
            .filter((c) => c.id && c.name);
          setCategories(cats);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading data for review:', error);
        showError('Failed to load transactions for review');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
    // showError is a toast helper; identity changes must not re-fetch the queue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reviewType, transactionIdsKey]);

  const resolveAlertIfNeeded = useCallback(async () => {
    if (!alertId) return;
    try {
      await api.post(`/alerts/${alertId}/resolve`);
    } catch (e) {
      console.error('Failed to resolve alert:', e);
    }
  }, [alertId]);

  // Sitting done: empty the loaded page. Only finish the queue (resolve alert +
  // "all reviewed") when nothing remains beyond this session (#1473).
  const checkCompletion = useCallback(async () => {
    if (transactions.length !== 0 || initialCount <= 0) return;

    const remainingBeyondSession = Math.max(0, queueTotal - initialCount);
    if (reviewType !== 'attribution' && remainingBeyondSession > 0) {
      // Ranked queue still has items. Mobile: "N more still need a look…".
      // Do not resolve the Activity alert or claim the queue is finished.
      showSuccess(
        `${remainingBeyondSession} more still need a look — I'll bring them up next time.`
      );
      onClose();
      return;
    }

    await resolveAlertIfNeeded();
    onAllReviewed?.();
    showSuccess(reviewType === 'attribution'
      ? 'All transactions attributed!'
      : 'All transactions reviewed!');
    onClose();
  }, [
    transactions.length,
    initialCount,
    queueTotal,
    resolveAlertIfNeeded,
    onAllReviewed,
    showSuccess,
    onClose,
    reviewType
  ]);

  useEffect(() => {
    if (!loading && initialCount > 0 && !categoryPickerOpen) {
      checkCompletion();
    }
  }, [loading, transactions.length, initialCount, checkCompletion, categoryPickerOpen]);

  const removeTransaction = useCallback((transactionId) => {
    setTransactions((prev) => prev.filter((t) => t._id !== transactionId));
    onTransactionReviewed?.(transactionId);
  }, [onTransactionReviewed]);

  // Keep — user agrees with AI categorization
  const handleApprove = useCallback(async (transactionId) => {
    if (!transactionId || processing[transactionId]) return;
    setProcessing((prev) => ({ ...prev, [transactionId]: true }));
    try {
      await api.post(`/ai/feedback/${transactionId}`, { approved: true });
      removeTransaction(transactionId);
      setCategoryPickerOpen(false);
      setActiveTransactionId(null);
      showSuccess('Transaction approved');
    } catch (error) {
      console.error('Error approving transaction:', error);
      showError('Failed to approve transaction');
    } finally {
      setProcessing((prev) => ({ ...prev, [transactionId]: false }));
    }
  }, [processing, removeTransaction, showSuccess, showError]);

  // Fix — open category picker for the focused transaction
  const handleOpenFix = useCallback((transactionId) => {
    if (!transactionId || processing[transactionId]) return;
    setActiveTransactionId(transactionId);
    setCategoryPickerOpen(true);
  }, [processing]);

  // Correction writes AIFeedback with correctedCategory and leaves the shared queue
  const handleCorrect = useCallback(async (transactionId, categoryName) => {
    if (!transactionId || !categoryName || processing[transactionId]) return;
    setProcessing((prev) => ({ ...prev, [transactionId]: true }));
    try {
      await api.post(`/ai/feedback/${transactionId}`, {
        approved: false,
        correctedCategory: categoryName
      });
      removeTransaction(transactionId);
      setCategoryPickerOpen(false);
      setActiveTransactionId(null);
      showSuccess(`Recategorized as ${categoryName}`);
    } catch (error) {
      console.error('Error correcting transaction:', error);
      const message = error?.response?.data?.error?.message
        || error?.response?.data?.error
        || 'Failed to save correction';
      showError(typeof message === 'string' ? message : 'Failed to save correction');
    } finally {
      setProcessing((prev) => ({ ...prev, [transactionId]: false }));
    }
  }, [processing, removeTransaction, showSuccess, showError]);

  // Keyboard parity: ← fix, → keep (when picker closed and a card is focused).
  // useLayoutEffect so the listener exists before the card is observable.
  // A passive effect runs after that paint, and the first arrow key is dropped.
  useLayoutEffect(() => {
    if (!open || !isQueueMode || loading || categoryPickerOpen) return undefined;
    if (!currentTransaction || isProcessingCurrent) return undefined;

    const onKeyDown = (event) => {
      // Ignore when focus is inside an input/textarea/select
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleApprove(currentTransaction._id);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handleOpenFix(currentTransaction._id);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    open,
    isQueueMode,
    loading,
    categoryPickerOpen,
    currentTransaction,
    isProcessingCurrent,
    handleApprove,
    handleOpenFix
  ]);

  // Handle assigning a transaction to a family member (for attribution)
  const handleAssign = async (transactionId, memberId, method = 'manual') => {
    setProcessing((prev) => ({ ...prev, [transactionId]: true }));
    try {
      await attributeFamilyTransaction(transactionId, { memberId, method });
      setTransactions((prev) => prev.filter((t) => t._id !== transactionId));
      onTransactionReviewed?.(transactionId);

      if (method === 'manual' && memberId) {
        const memberName = activeMembers.find((m) => (m.userId || m._id) === memberId)?.name || 'family member';
        showSuccess(`Assigned to ${memberName}`);
      } else if (method === 'ai') {
        showSuccess('Budgit AI will take another look');
      }
    } catch (error) {
      console.error('Error assigning transaction:', error);
      showError(error?.response?.data?.error || 'Failed to assign transaction');
    } finally {
      setProcessing((prev) => ({ ...prev, [transactionId]: false }));
    }
  };

  const formatAmount = (amount) => {
    const formatted = Math.abs(amount).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
    return amount > 0 ? `-${formatted}` : formatted;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const getConfidence = (transaction) => {
    if (reviewType === 'attribution') {
      return Math.round((transaction.attributionConfidence || 0) * 100);
    }
    return resolveConfidencePct(transaction) ?? 0;
  };

  const reviewedCount = initialCount > 0 ? initialCount - transactions.length : 0;
  const progress = initialCount > 0 ? (reviewedCount / initialCount) * 100 : 0;
  const remainingBeyondSession = Math.max(0, queueTotal - initialCount);

  const title = reviewType === 'attribution' ? 'Review Transactions' : 'Help Buddy Learn';
  const TitleIcon = reviewType === 'attribution' ? FamilyIcon : AutoAwesomeIcon;
  const emptyMessage = reviewType === 'attribution'
    ? 'There are no transactions waiting for review.'
    : 'Nothing needs a second opinion right now.';

  const pickerCategories = categories.filter((c) => {
    if (!currentTransaction || activeTransactionId !== currentTransaction._id) return true;
    const suggested = resolveSuggestedCategoryName(currentTransaction);
    return !suggested || c.name !== suggested;
  });

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      fullWidth
      aria-labelledby="transaction-review-title"
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TitleIcon color="primary" />
            <Typography id="transaction-review-title" variant="h6" component="span">
              {title}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close review">
            <CloseIcon />
          </IconButton>
        </Box>
        {initialCount > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {reviewedCount} of {initialCount} reviewed
                {remainingBeyondSession > 0 ? ` · ${remainingBeyondSession} more in queue` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(progress)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundColor: (theme) => theme.palette.success.main
                }
              }}
            />
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: isQueueMode ? 2 : 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : transactions.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              All caught up!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {emptyMessage}
            </Typography>
          </Box>
        ) : isQueueMode ? (
          <>
            {currentTransaction && !categoryPickerOpen && (
              <ReviewCard data-testid="review-queue-card">
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <MerchantLogo
                    src={currentTransaction.logoUrl}
                    alt={currentTransaction.merchantName || 'Transaction'}
                  >
                    {(currentTransaction.merchantName || currentTransaction.description)?.[0]?.toUpperCase()}
                  </MerchantLogo>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" noWrap sx={{ maxWidth: 280, fontWeight: 600 }}>
                        {currentTransaction.merchantName || currentTransaction.description}
                      </Typography>
                      {resolveConfidencePct(currentTransaction) != null && (
                        <ConfidenceBadge
                          size="small"
                          confidence={getConfidence(currentTransaction)}
                          label={`${getConfidence(currentTransaction)}%`}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(currentTransaction.authorizedDate || currentTransaction.date)}
                      </Typography>
                      {resolveSuggestedCategoryName(currentTransaction) && (
                        <>
                          <Typography variant="caption" color="text.secondary">•</Typography>
                          <Chip
                            size="small"
                            label={resolveSuggestedCategoryName(currentTransaction)}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </>
                      )}
                      {currentTransaction.account?.name && (
                        <>
                          <Typography variant="caption" color="text.secondary">•</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {currentTransaction.account.name}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color: currentTransaction.amount > 0 ? 'error.main' : 'success.main',
                      ml: 1,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {formatAmount(currentTransaction.amount)}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary">
                  I filed this under{' '}
                  <strong>{resolveSuggestedCategoryName(currentTransaction) || 'a category'}</strong>
                  {resolveConfidencePct(currentTransaction) != null
                    ? `, but I'm only ${getConfidence(currentTransaction)}% sure`
                    : ''}. Did I get it right?
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    disabled={isProcessingCurrent}
                    onClick={() => handleOpenFix(currentTransaction._id)}
                    startIcon={<ArrowBackIcon />}
                    sx={{ borderRadius: '50px', textTransform: 'none', py: 1.25 }}
                    aria-label="Fix category (Left arrow)"
                  >
                    Fix
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    disabled={isProcessingCurrent}
                    onClick={() => handleApprove(currentTransaction._id)}
                    startIcon={
                      isProcessingCurrent
                        ? <CircularProgress size={18} color="inherit" />
                        : <CheckIcon />
                    }
                    sx={{ borderRadius: '50px', textTransform: 'none', py: 1.25 }}
                    aria-label="Keep category (Right arrow)"
                  >
                    Keep
                    <ArrowForwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                  </Button>
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Keyboard: ← Fix · → Keep
                </Typography>
              </ReviewCard>
            )}

            {categoryPickerOpen && currentTransaction && (
              <Box data-testid="review-category-picker">
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Pick the right category
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {currentTransaction.merchantName || currentTransaction.description}
                  {resolveSuggestedCategoryName(currentTransaction)
                    ? ` · was ${resolveSuggestedCategoryName(currentTransaction)}`
                    : ''}
                </Typography>
                {categories.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No budget categories available. Add categories in your active budget first.
                  </Typography>
                ) : (
                  <List sx={{ maxHeight: 320, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 2 }}>
                    {pickerCategories.map((category) => (
                      <ListItemButton
                        key={category.id}
                        disabled={isProcessingCurrent}
                        onClick={() => handleCorrect(currentTransaction._id, category.name)}
                      >
                        <ListItemText primary={category.name} />
                      </ListItemButton>
                    ))}
                  </List>
                )}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    onClick={() => {
                      setCategoryPickerOpen(false);
                      setActiveTransactionId(null);
                    }}
                    sx={{ borderRadius: '50px', textTransform: 'none' }}
                  >
                    Back
                  </Button>
                </Box>
              </Box>
            )}
          </>
        ) : (
          <List sx={{ py: 0 }}>
            {transactions.map((transaction) => {
              const confidence = getConfidence(transaction);
              const isProcessing = processing[transaction._id];

              return (
                <TransactionRow key={transaction._id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <MerchantLogo
                      src={transaction.logoUrl}
                      alt={transaction.merchantName || 'Transaction'}
                    >
                      {(transaction.merchantName || transaction.description)?.[0]?.toUpperCase()}
                    </MerchantLogo>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" noWrap sx={{ maxWidth: 200 }}>
                            {transaction.merchantName || transaction.description}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(transaction.authorizedDate || transaction.date)}
                          </Typography>
                          {transaction.budgetCategory?.name && (
                            <>
                              <Typography variant="caption" color="text.secondary">
                                •
                              </Typography>
                              <Chip
                                size="small"
                                label={transaction.budgetCategory.name}
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </>
                          )}
                          {typeof confidence === 'number' && confidence > 0 && (
                            <ConfidenceBadge
                              size="small"
                              confidence={confidence}
                              label={`${confidence}%`}
                            />
                          )}
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: transaction.amount > 0 ? 'error.main' : 'success.main'
                        }}
                      >
                        {formatAmount(transaction.amount)}
                      </Typography>
                    </Box>
                  </Box>

                  {reviewType === 'attribution' && (
                    <Box sx={{ mt: 1.5, pl: 7 }}>
                      {activeMembers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Invite family members to start attributing transactions.
                        </Typography>
                      ) : (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {activeMembers.map((member) => {
                            const targetId = member.userId || member._id || '';
                            return (
                              <MemberButton
                                key={`${transaction._id}-${targetId}`}
                                variant="outlined"
                                size="small"
                                disabled={isProcessing}
                                onClick={() => handleAssign(transaction._id, targetId, 'manual')}
                              >
                                {isProcessing ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  member.name || member.email || member.role
                                )}
                              </MemberButton>
                            );
                          })}
                          <MemberButton
                            variant="text"
                            size="small"
                            color="primary"
                            disabled={isProcessing}
                            onClick={() => handleAssign(transaction._id, undefined, 'ai')}
                            startIcon={<AiIcon />}
                          >
                            Let Budgit decide
                          </MemberButton>
                        </Stack>
                      )}
                    </Box>
                  )}
                </TransactionRow>
              );
            })}
          </List>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: '50px' }}>
          Close
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default TransactionReviewModal;

// Exported for unit tests
export {
  REVIEW_QUEUE_LIMIT,
  resolveConfidencePct,
  resolveSuggestedCategoryName
};
